import React from 'react';
import { Table, Button } from 'react-bootstrap';
import { formatPrice } from '../../../../shared/utils/formatPrice';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { DailyResponse } from '../../../../services/revenue-report/response/DailyResponse';

interface RevenueTableReportProps {
    data: DailyResponse;
}

const RevenueTableReport: React.FC<RevenueTableReportProps> = ({ data }) => {
    const columns = [
        { label: "Date", key: "date" },
        { label: "Rooms", key: "total_rooms" },
        { label: "Room Sold", key: "total_booked" },
        { label: "Occupancy (%)", key: "occupancy" },
        { label: "Room Revenue (IDR)", key: "total_revenue_with_extra", format: true },
        { label: "ADR", key: "adr", format: true },
        { label: "RevPAR", key: "revpar", format: true },
        { label: "Room Only", key: "total_revenue", format: true },
        { label: "RevPAR Room Only", key: "revpar_room_only", format: true }
    ];

    // Export to PDF
    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text("Revenue Report - Daily", 14, 10);

        const bodyData = data.data.map(row => columns.map(col => {
            const value = row[col.key as keyof typeof row];
            return col.format ? formatPrice(Number(value)) : value;
        }));

        // Add total row
        const totalRow = [
            "Total",
            data.total.total_rooms,
            data.total.total_booked,
            `${data.total.occupancy}%`,
            formatPrice(Number(data.total.total_revenue_with_extra) || 0),
            formatPrice(Number(data.total.adr) || 0),
            formatPrice(Number(data.total.revpar) || 0),
            formatPrice(Number(data.total.total_revenue) || 0),
            formatPrice(Number(data.total.revpar_room_only) || 0)
        ];

        bodyData.push(totalRow);

        autoTable(doc, {
            head: [columns.map(col => col.label)],
            body: bodyData,
        });

        doc.save("RevenueReport_Daily.pdf");
    };

    // Export to Excel
    const exportToExcel = () => {
        const wsData = [
            columns.map(col => col.label),
            ...data.data.map(row => columns.map(col => {
                const value = row[col.key as keyof typeof row];
                return col.format ? formatPrice(Number(value)) : value;
            })),
        ];

        // Add total row
        const totalRow = [
            "Total",
            data.total.total_rooms,
            data.total.total_booked,
            `${data.total.occupancy}%`,
            formatPrice(Number(data.total.total_revenue_with_extra) || 0),
            formatPrice(Number(data.total.adr) || 0),
            formatPrice(Number(data.total.revpar) || 0),
            formatPrice(Number(data.total.total_revenue) || 0),
            formatPrice(Number(data.total.revpar_room_only) || 0)
        ];

        wsData.push(totalRow);

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const dataBlob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(dataBlob, "RevenueReport_Daily.xlsx");
    };

    return (
        <div className="mt-4">
            <div className="mb-3 d-flex gap-2">
                <Button variant="primary" onClick={exportToPDF}>Export to PDF</Button>
                <Button variant="success" onClick={exportToExcel}>Export to Excel</Button>
            </div>

            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            {columns.map((col, index) => (
                                <th key={index}>{col.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.data.length > 0 ? (
                            data.data.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {columns.map((col, colIndex) => {
                                        // const value = row[col.key as keyof typeof row];
                                        const value = col.key === "occupancy" ? `${row[col.key as keyof typeof row]}%` : row[col.key as keyof typeof row];
                                        return (
                                            <td key={colIndex}>
                                                {col.format ? formatPrice(Number(value)) : value}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length} className="text-center">No data available</td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td className="text-center">
                                <strong>Total</strong>
                            </td>
                            <td>
                                <strong>{data.total.total_rooms}</strong>
                            </td>
                            <td>
                                <strong>{data.total.total_booked}</strong>
                            </td>
                            <td>
                                <strong>{data.total.occupancy}%</strong>
                            </td>
                            <td>
                                <strong>{formatPrice(Number(data.total.total_revenue_with_extra) || 0)}</strong>
                            </td>
                            <td>
                                <strong>{formatPrice(Number(data.total.adr) || 0)}</strong>
                            </td>
                            <td>
                                <strong>{formatPrice(Number(data.total.revpar) || 0)}</strong>
                            </td>
                            <td>
                                <strong>{formatPrice(Number(data.total.total_revenue) || 0)}</strong>
                            </td>
                            <td>
                                <strong>{formatPrice(Number(data.total.revpar_room_only) || 0)}</strong>
                            </td>
                        </tr>
                    </tfoot>
                </Table>
            </div>
        </div>
    );
};

export default RevenueTableReport;